import './PageSkeleton.less';
import clsx from 'clsx';

export type PageSkeletonKind =
  'table' | 'dashboard' | 'details' | 'form' | 'canvas';

const COLUMNS = [28, 32, 18, 14, 8];
const CELL_WIDTHS = ['72%', '58%', '84%', '64%', '90%', '68%', '78%'];

type BoneProps = {
  width?: number | string;
  height?: number;
  className?: string;
};

export function Bone({ width, height, className }: Readonly<BoneProps>) {
  return (
    <span
      className={clsx('PageSkeleton__bone', className)}
      style={{ width, height }}
    />
  );
}

type TableSkeletonProps = {
  rows?: number;
  columns?: number[];
  className?: string;
};

export function TableSkeleton({
  rows = 7,
  columns = COLUMNS,
  className,
}: Readonly<TableSkeletonProps>) {
  const row = (index: number, head = false) => (
    <div
      key={index}
      className={clsx(
        'PageSkeleton__table-row',
        head && 'PageSkeleton__table-row--head',
      )}
    >
      {columns.map((flex, column) => (
        <div key={column} className="PageSkeleton__table-cell" style={{ flex }}>
          <Bone
            width={
              head ? '48%' : CELL_WIDTHS[(index + column) % CELL_WIDTHS.length]
            }
            height={head ? 12 : 14}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className={clsx('PageSkeleton__table', className)} aria-hidden>
      {row(-1, true)}
      {Array.from({ length: rows }, (_, index) => row(index))}
    </div>
  );
}

const header = (
  <div className="PageSkeleton__header">
    <Bone width="24%" height={24} />
    <Bone width="14%" />
  </div>
);

const toolbar = (
  <div className="PageSkeleton__toolbar">
    <Bone width={260} height={32} />
    <div className="PageSkeleton__actions">
      <Bone width={96} height={32} />
      <Bone width={96} height={32} />
    </div>
  </div>
);

const FILL_ROWS = 24;

const chartPanel = (key: number) => (
  <div key={key} className="PageSkeleton__panel">
    <Bone width="32%" height={16} />
    <Bone className="PageSkeleton__fill" height={240} />
  </div>
);

const KINDS: Record<Exclude<PageSkeletonKind, 'canvas'>, JSX.Element> = {
  table: (
    <>
      {header}
      {toolbar}
      <TableSkeleton rows={FILL_ROWS} className="PageSkeleton__grow" />
    </>
  ),
  dashboard: (
    <>
      {header}
      <div className="PageSkeleton__kpis">
        {[0, 1, 2, 3].map(key => (
          <div key={key} className="PageSkeleton__panel">
            <Bone width="46%" height={12} />
            <Bone width="62%" height={28} />
          </div>
        ))}
      </div>
      <div className="PageSkeleton__charts">
        {chartPanel(0)}
        {chartPanel(1)}
      </div>
      <div className="PageSkeleton__panel PageSkeleton__grow">
        <Bone width="24%" height={16} />
        <Bone className="PageSkeleton__fill PageSkeleton__grow" />
      </div>
    </>
  ),
  details: (
    <>
      <Bone width={88} />
      {header}
      <div className="PageSkeleton__panel PageSkeleton__facts">
        {[0, 1, 2, 3, 4, 5].map(key => (
          <div key={key} className="PageSkeleton__fact">
            <Bone width="36%" height={12} />
            <Bone width="64%" />
          </div>
        ))}
      </div>
      <div className="PageSkeleton__tabs">
        {[0, 1, 2, 3].map(key => (
          <Bone key={key} width={88} height={16} />
        ))}
      </div>
      <TableSkeleton rows={FILL_ROWS} className="PageSkeleton__grow" />
    </>
  ),
  form: (
    <>
      {header}
      <div className="PageSkeleton__panel PageSkeleton__form PageSkeleton__grow">
        {[0, 1, 2, 3, 4].map(key => (
          <div key={key} className="PageSkeleton__field">
            <Bone width={120} height={12} />
            <Bone className="PageSkeleton__fill" height={32} />
          </div>
        ))}
        <div className="PageSkeleton__actions PageSkeleton__actions--end">
          <Bone width={96} height={32} />
          <Bone width={96} height={32} />
        </div>
      </div>
    </>
  ),
};

export function PageSkeleton({
  kind = 'table',
}: Readonly<{ kind?: PageSkeletonKind }>) {
  if (kind === 'canvas') {
    return (
      <div className="PageSkeleton PageSkeleton--canvas" aria-busy>
        <Bone className="PageSkeleton__island" height={48} />
        <Bone className="PageSkeleton__stage" />
      </div>
    );
  }

  return (
    <div className={`PageSkeleton PageSkeleton--${kind}`} aria-busy>
      {KINDS[kind]}
    </div>
  );
}
